import { AfterViewInit, Component, Input, OnChanges } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { GetValueFromPpg } from 'src/app/perivis/shared/helpers/units.helper';

export interface BrineForm {
  name: string;
  type: string;
  brineType?: string;
  nominalDensity?: number;
  plasticViscosity?: number;
  viscosityTemperature?: number;
}

@Component({
    selector: 'app-brines',
    templateUrl: './brines.component.html',
    styles: [``],
    providers: subformComponentProviders(BrinesComponent),
    standalone: false
})
export class BrinesComponent implements AfterViewInit, OnChanges {

  @Input()
  public userUnits: UserUnitsModel;

  @Input()
  public isReadOnly: boolean;

  @Input()
  public selectedBrineType: string;

  @Input()
  public isInUse: boolean;

  @Input()
  public changeTrigger: boolean;
  
  public densityUnits: string;
  public densityValidation: { min: number, max: number };

  private _densityDefault: number;
  private _minDensity: number;
  private _maxDensity: number;

  public get nominalDensityControl(): UntypedFormControl {
    return this.form.formGroup.get('nominalDensity') as UntypedFormControl;
  }

  public brineTypes = [
    { label: 'Seawater', value: 'seawater' },
    { label: 'NaCl', value: 'nacl' },
    { label: 'KCl', value: 'kcl' },
    { label: 'CaCl2', value: 'cacl2' },
    { label: 'CaBr2', value: 'cabr2' },
    { label: 'ZnBr2', value: 'znbr2' },
    { label: 'KFo', value: 'kfo' },
    { label: 'CsFo', value: 'csfo' },
    { label: 'CsKFo', value: 'cskfo' },
    { label: 'CaCl2-NaCl', value: 'cacl2nacl' }
  ];

  ngOnChanges(): void {
    if (!this.userUnits) {
      return;
    }
    this.densityUnits = unitsLib[this.userUnits.density].symbol;
    this.getDefaultDensities();

    const minDensity = this._minDensity;
    const maxDensity = this._maxDensity;

    this.form.formGroup.controls.nominalDensity.clearValidators();
    this._densityDefault = GetValueFromPpg(this._minDensity, this.densityUnits);
    this.checkSeaWater();

    this.densityValidation = { min: minDensity, max: maxDensity};
    this.form.formGroup.controls.nominalDensity.setValidators([Validators.required, Validators.min(this.densityValidation.min), Validators.max(this.densityValidation.max)]);
    this.form.formGroup.controls.nominalDensity.updateValueAndValidity({ emitEvent: false });
  }

  ngAfterViewInit(): void {
    if (this._densityDefault) {
      this.checkSeaWater();
    }
  }

  public checkSeaWater(isUserChange = false): void {
    if (this.selectedBrineType === 'seawater' || this.form.formGroup.controls.name.value === 'Seawater') {
      // this.form.formGroup.controls.nominalDensity.patchValue(this._densityDefault, { emitEvent: false });
      this.form.formGroup.controls.nominalDensity.disable({ emitEvent: false });
    } else if (!this.isInUse && !this.form.formGroup.controls.nominalDensity.value) {
      this.form.formGroup.controls.nominalDensity.patchValue(this._densityDefault, { emitEvent: false });
      this.form.formGroup.controls.nominalDensity.enable({ emitEvent: false });
    } else if (!this.isInUse) {
      this.form.formGroup.controls.nominalDensity.enable({ emitEvent: false });
      this.form.formGroup.controls.nominalDensity.updateValueAndValidity({ emitEvent: false });
    }

    if (isUserChange) {
      this.getDefaultDensities();
      this.form.formGroup.controls.nominalDensity.patchValue(this._densityDefault);
    }
  }

  private getDefaultDensities(): void {
    switch(this.form.formGroup.controls.brineType.value) {
      case 'seawater': {
        this._densityDefault = +GetValueFromPpg(8.6, this.densityUnits).toFixed(2);
        this._minDensity = +GetValueFromPpg(8.6, this.densityUnits).toFixed(2);
        this._maxDensity = +GetValueFromPpg(8.6, this.densityUnits).toFixed(2);
        break;
      }
      case 'nacl': {
        this._densityDefault = GetValueFromPpg(8.5, this.densityUnits);
        this._minDensity = GetValueFromPpg(8.35, this.densityUnits);
        this._maxDensity = GetValueFromPpg(10, this.densityUnits);
        break;
      }
      case 'kcl': {
        this._densityDefault = GetValueFromPpg(8.5, this.densityUnits);
        this._minDensity = GetValueFromPpg(8.35, this.densityUnits);
        this._maxDensity = GetValueFromPpg(10, this.densityUnits);
        break;
      }
      case 'cacl2': {
        this._densityDefault = GetValueFromPpg(8.5, this.densityUnits);
        this._minDensity = GetValueFromPpg(8.35, this.densityUnits);
        this._maxDensity = GetValueFromPpg(12, this.densityUnits);
        break;
      }
      case 'cabr2': {
        this._densityDefault = GetValueFromPpg(11, this.densityUnits);
        this._minDensity = GetValueFromPpg(11, this.densityUnits);
        this._maxDensity = GetValueFromPpg(15.5, this.densityUnits);
        break;
      }
      case 'znbr2': {
        this._densityDefault = GetValueFromPpg(14.5, this.densityUnits);
        this._minDensity = GetValueFromPpg(14.5, this.densityUnits);
        this._maxDensity = GetValueFromPpg(19.2, this.densityUnits);
        break;
      }
      case 'kfo': {
        this._densityDefault = GetValueFromPpg(8.5, this.densityUnits);
        this._minDensity = GetValueFromPpg(8.35, this.densityUnits);
        this._maxDensity = GetValueFromPpg(14.2, this.densityUnits);
        break;
      }
      case 'csfo': {
        this._densityDefault = GetValueFromPpg(8.5, this.densityUnits);
        this._minDensity = GetValueFromPpg(8.35, this.densityUnits);
        this._maxDensity = GetValueFromPpg(20.8, this.densityUnits);
        break;
      }
      case 'cskfo': {
        this._densityDefault = GetValueFromPpg(13.1, this.densityUnits);
        this._minDensity = GetValueFromPpg(13.1, this.densityUnits);
        this._maxDensity = GetValueFromPpg(18.4, this.densityUnits);
        break;
      }
      case 'cacl2nacl': {
        this._densityDefault = GetValueFromPpg(8.35, this.densityUnits);
        this._minDensity = GetValueFromPpg(8.35, this.densityUnits);
        this._maxDensity = GetValueFromPpg(11.2, this.densityUnits);
        break;
      }
    }
  }

  public form = createForm<BrineForm>(this, {
    formType: FormType.SUB,
    formControls: {
      name: new UntypedFormControl(null),
      type: new UntypedFormControl(null),
      brineType: new UntypedFormControl(null, [Validators.required]),
      nominalDensity: new UntypedFormControl(null, { updateOn: 'blur' }),
      plasticViscosity: new UntypedFormControl(null, [Validators.required, Validators.min(0), Validators.max(2000)]),
      viscosityTemperature: new UntypedFormControl(null)
    }
  });

  public applyDefaults(): void {
    this.form.formGroup.controls.nominalDensity.setValue(this._densityDefault);
    // this.formGroupControls.plasticViscosity.setValue(1);
    // this.formGroupControls.viscosityTemperature.setValue(70);
  }
}
